import React, { useEffect, useState, useCallback, useRef } from 'react';
import colors from '../../constants/colors';
import twitterIcon from '../../assets/pictures/contact-twitter.png';
import gsIcon from '../../assets/pictures/contact-gs.png';
import ghIcon from '../../assets/pictures/contact-gh.png';
import inIcon from '../../assets/pictures/contact-in.png';
import marioPunch from '../../assets/pictures/mario-hit.gif';
import marioStill from '../../assets/pictures/mario-still.png';
import ResumeDownload from './ResumeDownload';

export interface ContactProps {}

// function to validate email
const validateEmail = (email: string) => {
    const re =
        // eslint-disable-next-line
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

interface SocialBoxProps {
    icon: string;
    link: string;
    onActivate?: () => void;
}

interface MarioAnimationProps {
    isAnimating: boolean;
    index: number;
}

const SocialBox: React.FC<SocialBoxProps> = ({ link, icon, onActivate }) => {
    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (onActivate) {
            onActivate();
        }
        
        setTimeout(() => {
            window.open(link, '_blank');
        }, 1000);
    };

    return (
        <a rel="noreferrer" target="_blank" href={link} onClick={handleClick}>
            <div className="big-button-container" style={styles.social}>
                <img src={icon} alt="" style={styles.socialImage} />
            </div>
        </a>
    );
};

const MarioAnimation: React.FC<MarioAnimationProps> = ({ isAnimating, index }) => {
    const animationKeyRef = useRef<number>(0);
    
    useEffect(() => {
        if (isAnimating) {
            // Increment key to force React to remount the image element
            // This ensures the GIF animation restarts every time
            animationKeyRef.current += 1;
        }
    }, [isAnimating]);
    
    // Use a key that changes when animation starts to force remount
    // This is the most reliable way to restart a GIF animation
    return (
        <img 
            key={`mario-${index}-${isAnimating ? animationKeyRef.current : 'still'}`}
            src={isAnimating ? marioPunch : marioStill}
            alt=""
            style={styles.marioImage}
        />
    );
};

const Contact: React.FC<ContactProps> = (props) => {
    const [company, setCompany] = useState('');
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [message, setMessage] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [formMessage, setFormMessage] = useState('');
    const [formMessageColor, setFormMessageColor] = useState('');
    const [activeMario, setActiveMario] = useState<number | null>(null);

    useEffect(() => {
        if (validateEmail(email) && name.length > 0 && message.length > 0) {
            setIsFormValid(true);
        } else {
            setIsFormValid(false);
        }
    }, [email, name, message]);

    async function submitForm() {
        if (!isFormValid) {
            setFormMessage('Form unable to validate, please try again.');
            setFormMessageColor('red');
            return;
        }
        try {
            setIsLoading(true);
            const res = await fetch(
                'https://formspree.io/f/xovadejr',
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        company,
                        email,
                        name,
                        message,
                    }),
                }
            );
            const data = await res.json();
            if (data.ok || data.success) {  // Check for both ok and success
                setFormMessage(`Message successfully sent. Thank you ${name}!`);
                // Reset form fields
                setCompany('');
                setEmail('');
                setName('');
                setMessage('');
                setFormMessageColor(colors.blue);
                setIsLoading(false);
                
                // Optional: scroll to top of form
                window.scrollTo(0, 0);
                
                // Optional: refresh the page after a short delay
                setTimeout(() => {
                    window.location.reload();
                }, 2000);  // 2 second delay to show the success message
            } else {
                setFormMessage(data.error || 'Error sending message. Please try again.');
                setFormMessageColor(colors.red);
                setIsLoading(false);
            }
        } catch (e) {
            setFormMessage(
                'There was an error sending your message. Please try again.'
            );
            setFormMessageColor(colors.red);
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (formMessage.length > 0) {
            setTimeout(() => {
                setFormMessage('');
                setFormMessageColor('');
            }, 4000);
        }
    }, [formMessage]);

    return (
        <div className="site-page-content">
            <div style={styles.header}>
                <h1>Contact</h1>
                <div style={styles.socialsContainer}>
                    <div style={styles.socials}>
                        <div style={styles.socialWrapper}>
                            <SocialBox
                                icon={ghIcon}
                                link={'https://github.com/M1shaaa'}
                                onActivate={() => {
                                    setActiveMario(0);
                                    setTimeout(() => setActiveMario(null), 500);
                                }}
                            />
                            <MarioAnimation
                                isAnimating={activeMario === 0}
                                index={0}
                            />
                        </div>
                        <div style={styles.socialWrapper}>
                            <SocialBox
                                icon={inIcon}
                                link={'https://www.linkedin.com/in/misha-o-keeffe-099348262/'}
                                onActivate={() => {
                                    setActiveMario(1);
                                    setTimeout(() => setActiveMario(null), 500);
                                }}
                            />
                            <MarioAnimation
                                isAnimating={activeMario === 1}
                                index={1}
                            />
                        </div>
                        <div style={styles.socialWrapper}>
                            <SocialBox
                                icon={twitterIcon}
                                link={'https://x.com/mish_uhhh'}
                                onActivate={() => {
                                    setActiveMario(2);
                                    setTimeout(() => setActiveMario(null), 500);
                                }}
                            />
                            <MarioAnimation
                                isAnimating={activeMario === 2}
                                index={2}
                            />
                        </div>
                        <div style={styles.socialWrapper}>
                            <SocialBox
                                icon={gsIcon}
                                link={'https://scholar.google.com/citations?hl=en&user=Q5ygtrwAAAAJ&view_op=list_works&sortby=pubdate'}
                                onActivate={() => {
                                    setActiveMario(3);
                                    setTimeout(() => setActiveMario(null), 500);
                                }}
                            />
                            <MarioAnimation
                                isAnimating={activeMario === 3}
                                index={3}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="text-block">
                <p>
                    I'm always happy to chat, so feel free to reach out!
                </p>
                <br />
                <p>
                    <b>Email: </b>
                    <a href="mailto:mokeeffe@stanford.edu">
                        mokeeffe@stanford.edu
                    </a>
                </p>

                <div style={styles.form}>
                    <label>
                        <p>
                            {!name && <span style={styles.star}>*</span>}
                            <b>Your name:</b>
                        </p>
                    </label>
                    <input
                        style={styles.formItem}
                        type="text"
                        name="name"
                        placeholder="Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <label>
                        <p>
                            {!validateEmail(email) && (
                                <span style={styles.star}>*</span>
                            )}
                            <b>Email:</b>
                        </p>
                    </label>
                    <input
                        style={styles.formItem}
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <label>
                        <p>
                            <b>Company (optional):</b>
                        </p>
                    </label>
                    <input
                        style={styles.formItem}
                        type="company"
                        name="company"
                        placeholder="Company"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                    />
                    <label>
                        <p>
                            {!message && <span style={styles.star}>*</span>}
                            <b>Message:</b>
                        </p>
                    </label>
                    <textarea
                        name="message"
                        placeholder="Message"
                        style={styles.formItem}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />
                    <div style={styles.buttons}>
                        <button
                            className="site-button"
                            style={styles.button}
                            type="submit"
                            disabled={!isFormValid || isLoading}
                            onMouseDown={submitForm}
                        >
                            {!isLoading ? (
                                'Send Message'
                            ) : (
                                <p className="loading">Sending</p>
                            )}
                        </button>
                        <div style={styles.formInfo}>
                            <p
                                style={Object.assign(
                                    {},
                                    { color: formMessageColor }
                                )}
                            >
                                <b>
                                    <sub>
                                        {formMessage
                                            ? `${formMessage}`
                                            : ' All messages get forwarded straight to my email'}
                                    </sub>
                                </b>
                            </p>
                            <p>
                                <sub>
                                    {!isFormValid ? (
                                        <span>
                                            <b style={styles.star}>*</b> =
                                            required
                                        </span>
                                    ) : (
                                        '\xa0'
                                    )}
                                </sub>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <ResumeDownload altText="Need a copy of my Resume?" />
        </div>
    );
};

const styles: StyleSheetCSS = {
    form: {
        flexDirection: 'column',
        marginTop: 32,
    },
    formItem: {
        marginTop: 4,
        marginBottom: 16,
    },
    socialImage: {
        width: 36,
        height: 36,
    },
    buttons: {
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    formInfo: {
        textAlign: 'right',

        flexDirection: 'column',
        alignItems: 'flex-end',
        paddingLeft: 24,
    },
    star: {
        paddingRight: 4,
        color: 'red',
    },
    button: {
        minWidth: 184,
        height: 32,
    },
    header: {
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    socialsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    socials: {
        marginBottom: 16,
        justifyContent: 'flex-end',
        display: 'flex',
        flexDirection: 'row',
        gap: '16px',
    },
    socialWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    social: {
        width: 36,
        height: 36,
        justifyContent: 'center',
        alignItems: 'center',
    },
    marioImage: {
        height: 32,
        width: 32,
        marginTop: 4,
    },
};

export default Contact;
